import { useState, useEffect } from 'react';
import { Download, Smartphone, Share, Plus, MoreVertical, Wifi, Bell, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { CloseIcon as XIcon } from './ui/close-icon';

export const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Public download page already offers the APK — never stack an install prompt on top of it.
    if (/^\/(download|install)(\/|$)/.test(window.location.pathname)) return;
    // Check if already installed as standalone
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);
    
    if (standalone) return;

    // Check if dismissed recently (within 12 hours)
    const dismissedAt = localStorage.getItem('pwa-install-dismissed');
    if (dismissedAt) {
      const hoursSinceDismiss = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60);
      if (hoursSinceDismiss < 12) return;
    }

    // Detect iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // For iOS, show custom instructions after delay
    if (isIOSDevice) {
      const timer = setTimeout(() => setShowPrompt(true), 2000);
      return () => clearTimeout(timer);
    }

    // For other browsers, listen for beforeinstallprompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show after user has been on site for a bit
      setTimeout(() => setShowPrompt(true), 2000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      console.log('[PWA] App installed successfully');
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('[PWA] User accepted install prompt');
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    setShowPrompt(false);
  };

  // Don't show if already installed or prompt not active
  if (isStandalone || !showPrompt) return null;

  return (
    <div 
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-[380px] z-50 animate-in slide-in-from-bottom-5 duration-300"
      data-testid="pwa-install-prompt"
    >
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-700 to-green-600 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <Smartphone className="w-5 h-5" />
            </div>
            <div>
              <p className="font-semibold text-sm">Install BBC Credits</p>
              <p className="text-xs text-white/80">For quick access</p>
            </div>
          </div>
          <button 
            onClick={handleDismiss}
            className="text-white/80 hover:text-white p-1.5 rounded-full hover:bg-white/20 transition-colors"
            aria-label="Dismiss"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {isIOS ? (
            // iOS Instructions
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Add this app to your home screen for instant access:
              </p>
              
              <div className="bg-gray-50 rounded-lg p-3 space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">1</div>
                  <div className="flex-1">
                    <span className="text-gray-700">Tap the </span>
                    <span className="inline-flex items-center bg-gray-200 rounded px-1.5 py-0.5 mx-1">
                      <Share className="w-4 h-4 text-blue-500" />
                    </span>
                    <span className="text-gray-700"> Share button</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">2</div>
                  <div className="flex-1">
                    <span className="text-gray-700">Scroll and tap </span>
                    <span className="inline-flex items-center bg-gray-200 rounded px-1.5 py-0.5 mx-1">
                      <Plus className="w-4 h-4" />
                      <span className="text-xs ml-1">Add to Home Screen</span>
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">3</div>
                  <span className="text-gray-700">Tap <strong>Add</strong> to confirm</span>
                </div>
              </div>

              <Button 
                onClick={handleDismiss}
                variant="outline"
                className="w-full"
              >
                Got it, thanks!
              </Button>
            </div>
          ) : (
            // Android/Desktop
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Install our app for the best experience with your loan management.
              </p>

              {/* Benefits */}
              <div className="grid grid-cols-3 gap-2 py-2">
                <div className="text-center p-2 bg-gray-50 rounded-lg">
                  <Wifi className="w-5 h-5 mx-auto text-green-600 mb-1" />
                  <p className="text-xs text-gray-600">Offline Access</p>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded-lg">
                  <Bell className="w-5 h-5 mx-auto text-green-600 mb-1" />
                  <p className="text-xs text-gray-600">Notifications</p>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded-lg">
                  <Zap className="w-5 h-5 mx-auto text-green-600 mb-1" />
                  <p className="text-xs text-gray-600">Fast Launch</p>
                </div>
              </div>

              {deferredPrompt ? (
                // Native install button
                <div className="space-y-2">
                  <Button 
                    onClick={handleInstall}
                    className="w-full bg-green-700 hover:bg-green-800 text-white h-11"
                    data-testid="pwa-install-button"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Install Now
                  </Button>
                  <Button 
                    onClick={handleDismiss}
                    variant="ghost"
                    className="w-full text-gray-500 text-sm"
                  >
                    Maybe later
                  </Button>
                </div>
              ) : (
                // Manual install instructions (fallback)
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700">To install manually:</p>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MoreVertical className="w-4 h-4" />
                      <span>Open browser menu</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Download className="w-4 h-4" />
                      <span>Select "Install app" or "Add to Home screen"</span>
                    </div>
                  </div>
                  <Button 
                    onClick={handleDismiss}
                    variant="outline"
                    className="w-full"
                  >
                    Got it
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
